import { Coordinates } from "custom-types/Coordinates";
import { Dispensary } from "custom-types/Dispensary";
import pino from "pino";
import { getMilesBetween } from "utils/distance";
import { toKm } from "@leafly-com/web-utils";
const logger = pino();

/*
 * This file is for functions that make it easier to work with Dispensary objects.
 * These are not selectors because they do not take state objects as input and are not memoized.
 */

const isClosedForTheWholeWeek = (weeklySchedule: {}) => {
  if (!weeklySchedule) {
    return true;
  }
  return Object.entries(weeklySchedule).every((OpenStatus: any) => {
    return OpenStatus && OpenStatus[1] && OpenStatus[1].isOpen === false;
  });
};

const getTodayWeekday = () => {
  return new Date()
    .toLocaleDateString("en-US", {
      weekday: "long",
    })
    .toLowerCase();
};

export const getWeeklySchedule = (dispensary?: Dispensary | null) =>
  dispensary?.schedule?.weekly;

export const getOpeningHours = (dispensary?: Dispensary | null) => {
  if (!dispensary) {
    return {
      hasHours: false,
      isOpenToday: false,
    };
  }

  try {
    const weeklySchedule = getWeeklySchedule(dispensary);

    if (!weeklySchedule) {
      return {
        hasHours: false,
        isOpenToday: false,
      };
    }

    const today = (weeklySchedule as any)[getTodayWeekday()];

    if (
      today &&
      Object.keys(today).length === 1 &&
      today.isOpen !== undefined
    ) {
      return {
        hasHours: false,
        isOpenToday: false,
      };
    }

    if (isClosedForTheWholeWeek(weeklySchedule)) {
      return {
        hasHours: false,
        isOpenToday: false,
      };
    }

    return {
      hasHours: true,
      isOpenToday: today.isOpen,
    };
  } catch (e) {
    logger.error(e.message);
    return {
      hasHours: false,
      isOpenToday: false,
    };
  }
};

const getMilesFromAnyDispensary = (
  dispensary?: Dispensary | null,
  userLocation?: Coordinates | null
) => {
  const { distanceMi, primaryLocation } = dispensary || {};

  if (distanceMi) {
    return distanceMi;
  } else if (primaryLocation) {
    try {
      return getMilesBetween(userLocation, primaryLocation, "miles")?.toFixed(
        1
      );
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  return null;
};

const convertToKilometers = (miles?: string | number | null) =>
  toKm(miles)?.toFixed(1);
const getLocalizedDistance = (
  miles: string | number | null,
  km: string | number,
  countryCode: string
) => {
  if (!miles) {
    return {
      shortened: null,
      full: null,
    };
  }
  return {
    shortened: countryCode === "CA" ? `${km} km` : `${miles} mi`,
    full: countryCode === "CA" ? `${km} km away` : `${miles} miles away`,
  };
};

export const getLocalizedDistanceAnyDispensary = (
  countryCode: string,
  dispensary?: Dispensary | null,
  location?: Coordinates | null
) => {
  const miles = getMilesFromAnyDispensary(dispensary, location);
  const km = convertToKilometers(miles);
  return getLocalizedDistance(miles, km, countryCode);
};
